import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
import { Network } from '@wren/shared';
import useCart from '../../hooks/useCart';
import useQueryParams from '../../hooks/useQueryParams';
import { spacing } from '../../style/theme';
import Button from '../buttons/Button';
import InputError from '../inputs/InputError';
import LoadingSpinner from '../UI/LoadingSpinner';
import { Button as RetryButton } from '../UI/button';
import StripeElementsWrapper from './StripeElementsWrapper';
import { validateCartForPayment } from '../../lib/checkout';
import { retryPastDueInvoice } from '../../lib/pastDueInvoiceRecovery';

interface Props {
  button?: ReactElement<React.HTMLProps<HTMLButtonElement>, 'button'>;
  secondaryButton?: ReactElement<React.HTMLProps<HTMLButtonElement>, 'button'>;
  forTeam?: boolean;
  teamId?: string;
  onChange?: (value: string) => void;
  redirectTo: string;
  redirectQueryParams?: { [key: string]: string };
  setupIntentClientSecret?: string | null;
  submittingText?: string;
  isCheckout: boolean; // We need to know this so we can close the cart
  recoverPastDueInvoice?: boolean;
  recoveryError?: string | null;
  onPostSetupFailure?: (message: string) => void;
}

export default function StripeSetupFormWrapper(props: Props) {
  const [clientSecret, setClientSecret] = useState<string | null | undefined>(
    props.setupIntentClientSecret
  );
  const [recoveryError, setRecoveryError] = useState<string | null>(null);
  const [setupIntentError, setSetupIntentError] = useState<string | null>(null);
  const [isCreatingSetupIntent, setIsCreatingSetupIntent] = useState(
    !props.setupIntentClientSecret
  );
  const setupIntentRequestRef = useRef<Promise<void> | null>(null);

  const resetSetupIntent = useCallback((message: string) => {
    // A successful SetupIntent cannot collect a replacement payment method.
    // Clear it so the wrapper fetches a fresh client secret and remounts
    // Elements while preserving the invoice failure for the user.
    setRecoveryError(message);
    setClientSecret(null);
  }, []);

  const createSetupIntent = useCallback(() => {
    if (setupIntentRequestRef.current) {
      return setupIntentRequestRef.current;
    }

    const request = (async () => {
      setIsCreatingSetupIntent(true);
      setSetupIntentError(null);

      try {
        const [response, responseBody] = await Network.get<{
          message?: string;
          clientSecret: string;
        }>('stripe/setup-intent-secret', {
          forTeam: props.forTeam || props.teamId ? 'true' : 'false',
          ...(props.teamId ? { teamId: props.teamId } : {}),
          ...(props.recoverPastDueInvoice
            ? { recoverPastDueInvoice: 'true' }
            : {}),
        });

        if (!response.ok || !responseBody?.clientSecret) {
          throw new Error(responseBody?.message);
        }

        setClientSecret(responseBody.clientSecret);
      } catch {
        setSetupIntentError(
          "We couldn't reload the payment form. Please try again."
        );
      }
    })();

    setupIntentRequestRef.current = request;
    void request.finally(() => {
      if (setupIntentRequestRef.current === request) {
        setupIntentRequestRef.current = null;
      }
      setIsCreatingSetupIntent(false);
    });
    return request;
  }, [props.forTeam, props.recoverPastDueInvoice, props.teamId]);

  useEffect(() => {
    if (!clientSecret) {
      void createSetupIntent();
    }
  }, [clientSecret, createSetupIntent]);

  if (!clientSecret) {
    return (
      <div className="space-y-4">
        {recoveryError && (
          <InputError injectCss={{ display: 'block' }} error={recoveryError} />
        )}
        {isCreatingSetupIntent ? (
          <div
            className="flex items-center justify-center gap-2 py-4 text-sm text-gray-4"
            role="status"
          >
            <LoadingSpinner color="currentColor" width={20} />
            Loading payment form…
          </div>
        ) : (
          <>
            <InputError
              injectCss={{ display: 'block' }}
              error={setupIntentError}
            />
            <RetryButton className="w-full" onClick={createSetupIntent}>
              Try again
            </RetryButton>
          </>
        )}
      </div>
    );
  }

  return (
    <StripeElementsWrapper key={clientSecret} options={{ clientSecret }}>
      <StripeSetupForm
        {...props}
        setupIntentClientSecret={clientSecret}
        recoveryError={recoveryError}
        onPostSetupFailure={resetSetupIntent}
      />
    </StripeElementsWrapper>
  );
}

function StripeSetupForm({
  button,
  secondaryButton,
  isCheckout,
  onChange,
  redirectTo,
  redirectQueryParams,
  recoverPastDueInvoice,
  setupIntentClientSecret,
  submittingText,
  teamId,
  recoveryError,
  onPostSetupFailure,
}: Props) {
  const { getQueryParam } = useQueryParams();
  const [error, setError] = useState<string | null | undefined>(
    recoveryError || getQueryParam('error')
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const elements = useElements();
  const { closeCart, openCart } = useCart();
  const stripe = useStripe();
  const handleChangePaymentMethodType = useCallback(
    (event: StripePaymentElementChangeEvent) => onChange?.(event.value.type),
    [onChange]
  );

  const getRedirectURL = useCallback(
    (additionalParams: Record<string, string> = {}) => {
      const rootRedirectUrl =
        process.env.NODE_ENV === 'development'
          ? 'http://localhost:3000'
          : `${window.location.origin}`;

      const redirectQueryParamObj = new URLSearchParams({
        ...redirectQueryParams,
        ...additionalParams,
      });

      return `${rootRedirectUrl}${redirectTo}?${redirectQueryParamObj.toString()}`;
    },
    [redirectQueryParams, redirectTo]
  );

  const finishInteractiveRecovery = useCallback(
    async ({
      setupIntentId,
      recoveryAttemptId,
      returnUrl,
    }: {
      setupIntentId: string;
      recoveryAttemptId: string;
      returnUrl: string;
    }) => {
      const recoveryResult = await retryPastDueInvoice({
        setupIntentId,
        recoveryAttemptId,
        teamId,
      });

      if (recoveryResult.status === 'declined') {
        setIsSubmitting(false);
        onPostSetupFailure?.(recoveryResult.message);
        return;
      }

      if (recoveryResult.status === 'requires_action') {
        const actionAttemptId = crypto.randomUUID();
        const actionReturnUrl = getRedirectURL({
          recoverPastDueInvoice: 'true',
          recoveryAttemptId: actionAttemptId,
          recoveryReturnPath: window.location.pathname,
          setup_intent: setupIntentId,
          setup_intent_client_secret: setupIntentClientSecret!,
          ...(teamId ? { teamId } : {}),
        });
        const { error: actionError, paymentIntent } =
          await stripe!.confirmPayment({
            clientSecret: recoveryResult.paymentIntentClientSecret,
            confirmParams: { return_url: actionReturnUrl },
            redirect: 'if_required',
          });
        if (actionError) {
          setIsSubmitting(false);
          onPostSetupFailure?.(
            actionError.message || 'We could not authenticate your payment.'
          );
          return;
        }

        if (paymentIntent.status === 'processing') {
          const processingUrl = new URL(actionReturnUrl);
          processingUrl.searchParams.set('recoveryStatus', 'processing');
          window.location.assign(processingUrl.toString());
          return;
        }

        return finishInteractiveRecovery({
          setupIntentId,
          recoveryAttemptId: actionAttemptId,
          returnUrl: actionReturnUrl,
        });
      }

      const completedUrl = new URL(returnUrl);
      completedUrl.searchParams.set('recoveryStatus', recoveryResult.status);
      window.location.assign(completedUrl.toString());
    },
    [
      getRedirectURL,
      onPostSetupFailure,
      setupIntentClientSecret,
      stripe,
      teamId,
    ]
  );

  const handleSubmit = useCallback(async () => {
    let closeCartPromise;
    let setupIntentSucceeded = false;
    try {
      if (isSubmitting || !elements || !stripe || !setupIntentClientSecret) {
        return;
      }

      setIsSubmitting(true);
      setError(null);

      if (isCheckout) {
        await validateCartForPayment();
        closeCartPromise = closeCart(); // We don't want to await this yet because ApplePay is impatient
      }

      if (recoverPastDueInvoice) {
        const recoveryAttemptId = crypto.randomUUID();
        const returnUrl = getRedirectURL({
          recoverPastDueInvoice: 'true',
          recoveryAttemptId,
          recoveryReturnPath: window.location.pathname,
          ...(teamId ? { teamId } : {}),
        });
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: 'if_required',
        });

        if (error) {
          setError(error.message);
          setIsSubmitting(false);
          return;
        }

        if (setupIntent.status === 'processing') {
          const processingUrl = new URL(returnUrl);
          processingUrl.searchParams.set('recoveryStatus', 'processing');
          window.location.assign(processingUrl.toString());
          return;
        }

        setupIntentSucceeded = true;
        await finishInteractiveRecovery({
          setupIntentId: setupIntent.id,
          recoveryAttemptId,
          returnUrl,
        });
        return;
      }

      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: { return_url: getRedirectURL() },
      });

      if (error) {
        if (closeCartPromise) {
          await closeCartPromise;
          await openCart();
        }
        setError(error.message);
        setIsSubmitting(false);
      }
    } catch (err) {
      if (closeCartPromise) {
        await closeCartPromise;
        await openCart();
      }
      const message = (err as Error).message;
      if (setupIntentSucceeded) {
        onPostSetupFailure?.(message);
      } else {
        setError(message);
      }
      setIsSubmitting(false);
    }
  }, [
    closeCart,
    elements,
    finishInteractiveRecovery,
    getRedirectURL,
    isCheckout,
    isSubmitting,
    openCart,
    onPostSetupFailure,
    recoverPastDueInvoice,
    setupIntentClientSecret,
    stripe,
    teamId,
  ]);

  const submitButton = button || (
    <Button size="medium" text="Submit">
      &nbsp;
    </Button>
  );

  return (
    <div>
      <PaymentElement
        id="stripe-card-element"
        onChange={handleChangePaymentMethodType}
      />

      <div css={{ marginTop: spacing.default }}>
        {error && (
          <InputError
            injectCss={{ margin: '16px 0', display: 'block' }}
            error={error}
          />
        )}

        <div
          className={
            secondaryButton
              ? 'flex items-center justify-between gap-4'
              : undefined
          }
        >
          {secondaryButton}
          {React.cloneElement(submitButton, {
            'aria-busy': isSubmitting,
            disabled: submitButton.props.disabled || isSubmitting,
            onClick: async (event: MouseEvent) => {
              await submitButton.props.onClick?.(event);
              return handleSubmit();
            },
            children:
              isSubmitting && submittingText ? (
                <>
                  <LoadingSpinner color="currentColor" width={18} />
                  {submittingText}
                </>
              ) : (
                submitButton.props.children
              ),
          })}
        </div>
      </div>
    </div>
  );
}
